import PropTypes from 'prop-types';

import {Box, Heading, Skeleton} from '@salesforce/retail-react-app/app/components/shared/ui';

import {Component} from '@amblique/king-living/app/components/contentful/component';

/**
 * Callout Block - Overlay (CF-024)
 * Full-width background image/video with centered subtitle and CTA overlay
 *
 * @typedef {import('@chakra-ui/styled-system').SystemStyleObject} SystemStyleObject
 * @typedef {"wrapper" | "backgroundWrapper" | "backgroundMedia" | "contentWrapper" | "subtitle" | "actionLinks"} CalloutBlockOverlayParts
 * @typedef {Partial<Record<CalloutBlockOverlayParts, SystemStyleObject>>} CalloutBlockOverlayStyles
 *
 * @param {Object} props - Component props
 * @param {Contentful.HeroBanner} props.component - HeroBanner component data
 * @param {boolean} [props.isComplete] - Whether component has all fields
 * @param {boolean} [props.isFetching] - Whether additional data is being fetched
 * @param {CalloutBlockOverlayStyles} [props.styles] - Component styles
 * @returns {React.ReactElement}
 */
export const CalloutBlockOverlayCF024 = ({component, isComplete = true, isFetching = false, styles, ...props}) => {
  const {backgroundMedia, subtitle, cta} = component;

  // Determine which background to use (Image has priority over Video)
  const backgroundImage = backgroundMedia?.__typename === 'Image' ? backgroundMedia : null;
  const backgroundVideo = backgroundMedia?.__typename === 'Video' ? backgroundMedia : null;

  return (
    <Box {...styles.wrapper}>
      {/* Background Media Layer */}
      <Box className="hero-image-wrapper" {...styles.backgroundWrapper}>
        {backgroundImage ? (
          <Component component={backgroundImage} {...styles.backgroundMedia} />
        ) : backgroundVideo ? (
          <Component component={backgroundVideo} {...styles.backgroundMedia} />
        ) : (
          <Skeleton height="100%" width="100%" />
        )}
      </Box>

      {/* Content Overlay Layer - Centered */}
      <Box className="hero-content-wrapper" {...styles.contentWrapper}>
        {!isComplete && isFetching ? (
          <Skeleton height="120px" />
        ) : (
          <>
            {subtitle && <Heading {...styles.subtitle}>{subtitle}</Heading>}
            {cta && <Component component={cta} {...styles.actionLinks} />}
          </>
        )}
      </Box>
    </Box>
  );
};

CalloutBlockOverlayCF024.displayName = 'CalloutBlockOverlayCF024';
CalloutBlockOverlayCF024.propTypes = {
  component: PropTypes.object.isRequired,
  isComplete: PropTypes.bool,
  isFetching: PropTypes.bool,
  styles: PropTypes.object.isRequired,
};
